import { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { T } from "@/components/editable/T";
import { parseI18nKey } from "@/lib/i18nKeyUtils";
import { useTranslation } from "react-i18next";

interface FAQItem {
  questionKey: string;
  questionDefault: string;
  answerKey: string;
  answerDefault: string;
}

interface FAQProps {
  titleKey?: string;
  titleDefault?: string;
  subtitleKey?: string;
  subtitleDefault?: string;
  items: FAQItem[];
  className?: string;
  style?: React.CSSProperties;
  dark?: boolean;
  /** Alignment of the section header (and container width) */
  align?: "center" | "left";
  /** i18n page prefix for FAQ title/subtitle (defaults to extracting from titleKey) */
  page?: string;
  /** Override the heading size classes (e.g. "feature-section-heading") */
  headingClassName?: string;
}

export function FAQ({
  titleKey = "faq.title",
  titleDefault = "Veelgestelde vragen",
  subtitleKey = "faq.subtitle",
  subtitleDefault = "Heb je nog vragen? Hier vind je antwoorden op de meestgestelde vragen.",
  items,
  className = "",
  style,
  dark = false,
  align = "center",
  page: pageProp,
  headingClassName,
}: FAQProps) {

  const { t } = useTranslation();

  // Derive page/key: prefer explicit pageProp, otherwise split on first dot
  const parsedTitle = parseI18nKey(titleKey);
  const titlePage = pageProp || parsedTitle.page;
  const titleK = pageProp ? titleKey.replace(`${pageProp}.`, '') : parsedTitle.key;
  const parsedSubtitle = parseI18nKey(subtitleKey);
  const subtitleK = pageProp ? subtitleKey.replace(`${pageProp}.`, '') : parsedSubtitle.key;

  // Inject FAQ JSON-LD structured data
  useEffect(() => {
    const faqEntries = items.map((item) => {
      const qI18n = `${pageProp || parseI18nKey(item.questionKey).page}.${pageProp ? item.questionKey.replace(`${pageProp}.`, '') : parseI18nKey(item.questionKey).key}`;
      const aI18n = `${pageProp || parseI18nKey(item.answerKey).page}.${pageProp ? item.answerKey.replace(`${pageProp}.`, '') : parseI18nKey(item.answerKey).key}`;
      const q = t(qI18n, { defaultValue: item.questionDefault });
      const a = t(aI18n, { defaultValue: item.answerDefault });
      return {
        "@type": "Question" as const,
        name: q,
        acceptedAnswer: { "@type": "Answer" as const, text: a },
      };
    });

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqEntries,
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [items, t, pageProp]);

  return (
    <section className={`pt-[100px] pb-20 px-6 rounded-3xl ${className}`} style={style}>
      <div className={align === 'left' ? 'w-full sm:w-[80%] max-w-7xl mx-auto' : 'max-w-4xl mx-auto'}>
        <div className={align === 'left' ? 'text-left mb-12' : 'text-center mb-12'}>
          <T page={titlePage} k={titleK} d={titleDefault} as="h2" className={`${headingClassName ?? (align === 'left' ? 'feature-section-heading' : 'text-3xl md:text-4xl font-semibold')} mb-4 ${dark ? 'text-white' : 'text-foreground'}`} />
          <T page={titlePage} k={subtitleK} d={subtitleDefault} as="p" className={`text-lg max-w-2xl ${align === 'left' ? '' : 'mx-auto'} ${dark ? 'text-white/70' : 'text-muted-foreground'}`} />
        </div>


        <Accordion type="single" collapsible className="w-full space-y-4">
          {items.map((item, index) => {
            const parsedQ = parseI18nKey(item.questionKey);
            const qPage = pageProp || parsedQ.page;
            const qK = pageProp ? item.questionKey.replace(`${pageProp}.`, '') : parsedQ.key;
            const parsedA = parseI18nKey(item.answerKey);
            const aPage = pageProp || parsedA.page;
            const aK = pageProp ? item.answerKey.replace(`${pageProp}.`, '') : parsedA.key;

            return (
              <AccordionItem 
                key={index} 
                value={`item-${index + 1}`} 
                className={`rounded-2xl px-6 ${dark ? 'bg-white/10 border-white/10' : 'bg-white border border-border'}`}
              >
                <AccordionTrigger className={`text-left text-base font-medium hover:no-underline py-5 ${dark ? 'text-white' : 'text-foreground'}`}>
                  <T page={qPage} k={qK} d={item.questionDefault} />
                </AccordionTrigger>
                <AccordionContent className={`pb-5 ${dark ? 'text-white/70' : 'text-muted-foreground'}`}>
                  <T page={aPage} k={aK} d={item.answerDefault} />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </section>
  );
}
